import { inject, Injectable } from '@angular/core';
import { NgbModal, NgbModalOptions, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';

import {
  ConfirmDialogType,
  QPointConfirmDialogButtonConfig,
  QPointConfirmDialogButtonValue,
  QPointConfirmDialogComponent
} from './confirm-dialog.component';
import { ButtonVariant } from '@qpoint/buttons';
import { Observable, pipe, UnaryFunction } from 'rxjs';
import { map } from 'rxjs/operators';

export type QPointConfirmDialogOptions = NgbModalOptions & {
  buttons?: QPointConfirmDialogButtonConfig[];
  title?: string;
  message: string;
  confirmDialogType?: ConfirmDialogType;
};

/**
 * provides three different button config presets:
 * - single: config for a single button with text "ok"
 * - default: config for two buttons, one with "accept" and the other with "no"
 * - three: config for three buttons, one with "accept", the second with "no" and the last one with "cancel"
 */
export const QPointConfirmDialogButtonPreset: Record<'single' | 'default' | 'three', QPointConfirmDialogButtonConfig[]> = {
  single: [
    {
      value: true,
      text: 'common.ok',
      variant: ButtonVariant.primary
    },
  ],
  default: [
    {
      value: true,
      text: 'common.accept',
      variant: ButtonVariant.primary
    },
    {
      value: false,
      text: 'common.no',
      variant: ButtonVariant.secondary
    },
  ],
  three: [
    {
      value: true,
      text: 'common.accept',
      variant: ButtonVariant.primary
    },
    {
      value: false,
      text: 'common.no',
      variant: ButtonVariant.secondary
    },
    {
      value: null,
      text: 'common.cancel',
      variant: ButtonVariant.secondary
    }
  ]
};

export function qpointConfirmClosedValueOnly():
  UnaryFunction<Observable<QPointConfirmDialogButtonConfig>, Observable<QPointConfirmDialogButtonValue>> {
  return pipe(
    map((result) => result?.value as QPointConfirmDialogButtonValue)
  );
}

export class QPointNgbModalRef {
  constructor(public modalRef: NgbModalRef) {
  }

  get closed(): Observable<QPointConfirmDialogButtonConfig> {
    return this.modalRef.closed;
  }

  get valueOnlyClosed(): Observable<QPointConfirmDialogButtonValue> {
    return this.modalRef.closed.pipe(
      qpointConfirmClosedValueOnly()
    );
  }

  get result(): Promise<QPointConfirmDialogButtonConfig> {
    return this.modalRef.result as Promise<QPointConfirmDialogButtonConfig>;
  }

  get valueOnlyResult(): Promise<QPointConfirmDialogButtonValue> {
    return this.result
      .catch(() => {
        return {
          value: undefined
        };
      })
      .then(
        res => res.value ?? null
      );
  }
}

export function injectQPointConfirmDialogService(): QPointConfirmDialogService {
  return inject(QPointConfirmDialogService);
}

@Injectable({
  providedIn: 'root'
})
export class QPointConfirmDialogService {
  private modalService = inject(NgbModal);
  dialogRes = true;

  /**
   * @param options
   */
  openConfirm(options: QPointConfirmDialogOptions): QPointNgbModalRef {
    const modalRef = this.modalService.open(QPointConfirmDialogComponent, {
      ...options,
      centered: true,
      windowClass: 'qpoint-dialog confirm-dialog',
    });

    const componentInstance: QPointConfirmDialogComponent = modalRef.componentInstance;
    componentInstance.buttons = options.buttons || QPointConfirmDialogButtonPreset.default;
    componentInstance.confirmDialogType = options.confirmDialogType;
    componentInstance.title = options.title;
    componentInstance.message = options.message;

    return new QPointNgbModalRef(modalRef);
  }

  /**
   * @deprecated use openConfirm() instead
   * @param buttons
   * @param title
   * @param message
   * @param btn1Text
   * @param btn2Text
   * @param btn3Text
   * @param dialogSize
   * @param confirmDialogType
   * @param primaryButtonNumber
   * @param container
   * @private
   */
  private async confirmAsync(
    buttons: number = 1,
    title: string = null,
    message: string,
    btn1Text: string = (buttons > 2) ? 'common.yes' : (buttons > 1) ? 'common.accept' : 'common.ok',
    btn2Text: string = 'common.no',
    btn3Text: string = 'common.cancel',
    dialogSize: NgbModalOptions["size"] = 'md',
    confirmDialogType: ConfirmDialogType = null,
    primaryButtonNumber: number = 1,
    container?: HTMLElement | string
  ): Promise<void> {
    this.dialogRes = false;
    await this.confirmDialog(buttons, title, message, btn1Text, btn2Text, btn3Text, dialogSize, confirmDialogType, primaryButtonNumber, container).result
      .then((confirmed: QPointConfirmDialogButtonConfig) => {
        this.dialogRes = confirmed.value === 0;
      })
      .catch(error => {
        this.dialogRes = error;
      });
  }

  /**
   * @deprecated use openConfirm() instead
   * @param buttons
   * @param title
   * @param message
   * @param btn1Text
   * @param btn2Text
   * @param btn3Text
   * @param dialogSize
   * @param confirmDialogType
   * @param primaryButtonNumber
   * @param container
   */
  public async confirm(
    buttons: number = 1,
    title: string = null,
    message: string,
    btn1Text: string = (buttons > 2) ? 'common.yes' : (buttons > 1) ? 'common.accept' : 'common.ok',
    btn2Text: string = 'common.no',
    btn3Text: string = 'common.cancel',
    dialogSize: NgbModalOptions["size"] = 'md',
    confirmDialogType: ConfirmDialogType = null,
    primaryButtonNumber: number = 1,
    container?: HTMLElement | string
  ): Promise<boolean> {
    await this.confirmAsync(buttons, title, message, btn1Text, btn2Text, btn3Text, dialogSize, confirmDialogType, primaryButtonNumber, container);
    return this.dialogRes;
  }

  /**
   * @deprecated use openConfirm() instead
   * @param buttons
   * @param title
   * @param message
   * @param btn1Text
   * @param btn2Text
   * @param btn3Text
   * @param dialogSize
   * @param confirmDialogType
   * @param primaryButtonNumber
   * @param container
   */
  confirmWithRef(
    buttons: number = 1,
    title: string = null,
    message: string,
    btn1Text: string = (buttons > 2) ? 'common.yes' : (buttons > 1) ? 'common.accept' : 'common.ok',
    btn2Text: string = 'common.no',
    btn3Text: string = 'common.cancel',
    dialogSize: NgbModalOptions["size"] = 'md',
    confirmDialogType: ConfirmDialogType = null,
    primaryButtonNumber: number = 1,
    container?: HTMLElement | string,
  ): NgbModalRef {
    return this.confirmDialog(buttons, title, message, btn1Text, btn2Text, btn3Text, dialogSize, confirmDialogType, primaryButtonNumber, container);
  }

  /**
   * @deprecated use openConfirm() instead
   * @param buttons
   * @param title
   * @param message
   * @param btn1Text
   * @param btn2Text
   * @param btn3Text
   * @param dialogSize
   * @param confirmDialogType
   * @param primaryButtonNumber
   * @param container
   * @private
   */
  private confirmDialog(
    buttons: number = 1,
    title: string = null,
    message: string,
    btn1Text: string = (buttons > 2) ? 'common.yes' : (buttons > 1) ? 'common.accept' : 'common.ok',
    btn2Text: string = 'common.no',
    btn3Text: string = 'common.cancel',
    dialogSize: NgbModalOptions["size"] = 'md',
    confirmDialogType: ConfirmDialogType = null,
    primaryButtonNumber: number = 1,
    container?: HTMLElement | string
  ): NgbModalRef {
    const modalRef = this.modalService.open(QPointConfirmDialogComponent, {
      size: dialogSize,
      centered: true,
      windowClass: 'qpoint-dialog confirm-dialog',
      container
    });

    if (title) {
      modalRef.componentInstance.title = title;
      modalRef.componentInstance.message = message;
    } else {
      modalRef.componentInstance.title = message;
    }

    modalRef.componentInstance.buttons = [
      {
        value: 0,
        text: btn1Text,
        variant: primaryButtonNumber === 1 ? ButtonVariant.primary : ButtonVariant.secondary
      },
      {
        value: 1,
        text: btn2Text,
        variant: primaryButtonNumber === 2 ? ButtonVariant.primary : ButtonVariant.secondary
      },
      {
        value: 2,
        text: btn3Text,
        variant: primaryButtonNumber === 3 ? ButtonVariant.primary : ButtonVariant.secondary
      },
    ];

    modalRef.componentInstance.confirmDialogType = confirmDialogType;

    return modalRef;
  }
}
